/**
 * API Utility with JWT Authentication
 * Automatically adds JWT token to all API requests
 */
import axios from 'axios';
import { API_URL } from '../config/api';

// Create axios instance with default config
const api = axios.create({
  baseURL: API_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Request interceptor to add JWT token
api.interceptors.request.use(
  (config) => {
    // Get token from localStorage
    const token = localStorage.getItem('jwtToken');
    
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    
    // Add organization slug header as fallback
    const orgSlug = localStorage.getItem('organizationSlug');
    if (orgSlug) {
      config.headers['X-Organization-Slug'] = orgSlug;
    }
    
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Response interceptor to handle errors
api.interceptors.response.use(
  (response) => response,
  (error) => {
    // Handle 401 Unauthorized - redirect to login
    // BUT skip redirect for certain routes that handle auth themselves
    if (error.response && error.response.status === 401) {
      const requestUrl = error.config?.url || '';
      const currentPath = window.location.pathname;
      
      // Routes that should NOT auto-redirect to login on 401
      // These either handle their own auth or shouldn't disrupt the user
      const skipRedirectRoutes = [
        '/public/',           // All public API endpoints
        '/customer-ticket',   // Customer ticket page
        '/ticket/',           // Ticket alias
        '/check-in/',         // Check-in page
        '/self-check-in',     // Self check-in
        '/book',              // Booking widget
        '/booking',           // Booking page
        '/timeclock',         // Timeclock - NEVER auto-logout, let component handle gracefully
        '/services',          // Services - don't logout on empty services
        '/assets',            // Assets - don't logout on empty assets
        '/organizations/me',  // Organization context - handle gracefully
        '/organizations/',    // Organization details
        '/embed-tokens',      // Embed token operations
        '/team',              // Team members
        '/bookings'           // Bookings - don't logout on empty bookings
      ];
      
      const shouldSkipRedirect = skipRedirectRoutes.some(route => 
        requestUrl.includes(route) || currentPath.includes(route)
      );
      
      if (!shouldSkipRedirect) {
        // Only redirect to login for protected routes
        localStorage.removeItem('jwtToken');
        localStorage.removeItem('userType');
        localStorage.removeItem('userData');
        localStorage.removeItem('organizationId');
        localStorage.removeItem('organizationSlug');
        
        window.location.href = '/login';
      }
      // For skipped routes, just reject the promise and let the component handle it
    }
    
    return Promise.reject(error);
  }
);

export default api;

// Export convenience methods
export const apiGet = (url, config) => api.get(url, config);
export const apiPost = (url, data, config) => api.post(url, data, config);
export const apiPut = (url, data, config) => api.put(url, data, config);
export const apiPatch = (url, data, config) => api.patch(url, data, config);
export const apiDelete = (url, config) => api.delete(url, config);
